// Messaging data layer (Module 17 §5, slice P3-B part 2). TanStack Query
// hooks over the generated api-client — doctrine 3: apps/web talks to the API
// only through `@assessly/api-client`, never a hand-rolled fetch. Every
// mutation sends a fresh Idempotency-Key (§5: "mutations require
// Idempotency-Key") and invalidates the lists it affects; every queryFn
// threads `{signal}` so an unmounted screen's read is actually cancelled.
//
// THE ONE fetch that is not the api-client: the presigned S3 PUT the
// upload-url flow answers with (§5 /media/upload-url). S3 is not our API and
// has no path in the client; the PUT goes straight at the signed URL, exactly
// as the floor capture path uploads evidence. Everything around it — minting
// the URL, sending the key on the reply — rides the client.
//
// CACHE SHAPE. Conversation LIST pages are cursor-paginated and one page per
// query; accumulation (foldPagedRows) is the SCREEN's, so a refetch of page
// one updates rows instead of duplicating them. A thread's MESSAGE pages are
// one infinite query per conversation (D-322 ruling 7), because a settled
// thread must re-read every page it has loaded, and TanStack refetches an
// infinite query's pages in order from page one. A send/patch/read
// invalidates the thread head, the message pages and the list — the §4.1
// unread badge and preview line live on the list rows, so a stale list is a
// lie the sales manager acts on.

import type { Locale } from "@assessly/contracts/locales";
import { useSyncExternalStore } from "react";
import {
  onlineManager,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { newIdempotencyKey, unwrap, RequestError } from "../../../../lib/request";

type S = components["schemas"];

export type ChannelAccount = S["ChannelAccount"];
export type ProviderHealth = S["MessagingProviderHealth"];
export type ChannelAccountProbe = S["ChannelAccountProbeResponse"];
export type MessagingHealthSnapshot = S["MessagingHealthSnapshot"];
export type ChannelIdentity = S["ChannelIdentity"];
export type LinkCodeIssue = S["LinkCodeIssueResponse"];
export type MessageTemplate = S["MessageTemplate"];
export type MessageTemplateVariant = S["MessageTemplateVariant"];
export type TemplateVariable = S["MessagingTemplateVariable"];
export type TemplateButton = S["MessagingTemplateButton"];
export type TemplateCreateRequest = S["MessagingTemplateCreateRequest"];
export type TemplateUpdateRequest = S["MessagingTemplateUpdateRequest"];
export type VariantPutRequest = S["TemplateVariantPutRequest"];
export type Conversation = S["MessagingConversation"];
/** One page of the §4.1 inbox list, as `GET /v1/messaging/conversations` serves it. */
export interface ConversationPage {
  items: Conversation[];
  next_cursor: string | null;
}
export type ThreadMessage = S["MessagingThreadMessage"];
export type MessageMediaItem = S["MessagingMessageMediaItem"];
export type ThreadSendRequest = S["MessagingThreadSendRequest"];
export type ConversationPatchRequest = S["MessagingConversationPatchRequest"];
export type WebhookEventLogItem = S["MessagingWebhookEventLogItem"];
export type WaWindow = S["MessagingWaWindow"];
export type EmailIntake = S["MessagingEmailIntake"];
export type EmailQuarantineItem = S["MessagingEmailQuarantineItem"];
export type EmailHeldPart = S["MessagingEmailHeldPart"];
export type EmailOmittedPart = S["MessagingEmailOmittedPart"];

export type ConversationsQuery = NonNullable<
  paths["/v1/messaging/conversations"]["get"]["parameters"]["query"]
>;
export type TemplatesQuery = NonNullable<
  paths["/v1/messaging/templates"]["get"]["parameters"]["query"]
>;
export type IdentitiesQuery = NonNullable<
  paths["/v1/messaging/identities"]["get"]["parameters"]["query"]
>;

export const messagingKeys = {
  all: ["messaging"] as const,
  conversations: (query: ConversationsQuery, cursor: string | undefined) =>
    ["messaging", "conversations", query, cursor ?? null] as const,
  conversationLists: () => ["messaging", "conversations"] as const,
  conversation: (id: string) => ["messaging", "conversation", id] as const,
  threadPages: (id: string) => ["messaging", "thread", id, "pages"] as const,
  thread: (id: string) => ["messaging", "thread", id] as const,
  accounts: () => ["messaging", "accounts"] as const,
  health: () => ["messaging", "health"] as const,
  webhookLog: () => ["messaging", "webhook-log"] as const,
  templates: (query: TemplatesQuery) => ["messaging", "templates", query] as const,
  templateLists: () => ["messaging", "templates"] as const,
  identities: (query: IdentitiesQuery) => ["messaging", "identities", query] as const,
  identityLists: () => ["messaging", "identities"] as const,
  emailIntake: () => ["messaging", "email-intake"] as const,
  emailQuarantine: () => ["messaging", "email-quarantine"] as const,
  conversationHeads: () => ["messaging", "conversation"] as const,
  threads: () => ["messaging", "thread"] as const,
};

// ---------------------------------------------------------------------------
// conversations — the §4.1 inbox (PART-2 routes)
// ---------------------------------------------------------------------------

/** The inbox list. `refetchInterval` keeps the ≤10-manager shared inbox
 * honest without SSE plumbing this slice does not have: a minute-scale poll,
 * only while the tab is visible (react-query's default focus behaviour). */
export function useConversations(
  query: ConversationsQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery<ConversationPage>({
    queryKey: messagingKeys.conversations(query, cursor),
    enabled,
    refetchInterval: 60_000,
    // A page turn (same tab and search, another cursor) keeps the page shown
    // until the next one lands, so the list never drops to a skeleton between
    // pages. A new tab or search starts clean: another filter's threads are
    // never shown as this one's.
    placeholderData: (previous, previousQuery) =>
      previousQuery !== undefined &&
      JSON.stringify(previousQuery.queryKey[2]) === JSON.stringify(query)
        ? previous
        : undefined,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/conversations", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the conversations");
    },
  });
}

/** The §4.1 thread head: conversation + identity (masked per §7) + customer
 * summary + the computed `wa_window` — served computed so no client re-derives
 * 24-hour arithmetic. */
export function useConversation(id: string, enabled = true) {
  return useQuery({
    queryKey: messagingKeys.conversation(id),
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/conversations/{id}", {
        signal,
        params: { path: { id } },
      });
      return unwrap(res, "Could not load the thread");
    },
  });
}

/**
 * A message whose tick can still MOVE: `queued` (waiting for the send sweep)
 * or `sending` (claimed, on the wire). Every other status is terminal for the
 * thread's purposes — `sent`/`delivered`/`read` only ever advance through the
 * provider's own callbacks, and the failures and `suppressed` are resting
 * states with a Retry button, not a spinner.
 */
const IN_FLIGHT = new Set<string>(["queued", "sending"]);

/** How often the thread page refetches, given what it currently holds. */
export const THREAD_LIVE_INTERVAL_MS = 2_000;

/** The lifetime of a thread media link. A registry file's download URL is the
 * file registry's 5-minute presigned GET (D-322). A row that still holds its
 * pre-0076 key gets 15 minutes, and the shorter one sets the pace. */
export const MEDIA_LINK_LIFETIME_MS = 5 * 60_000;

/** How often a settled thread that shows media links refetches, so a link is
 * re-minted before it expires. One minute of margin under the lifetime covers
 * a slow response and a click on a link painted just before the refetch. */
export const THREAD_MEDIA_LINK_INTERVAL_MS = MEDIA_LINK_LIFETIME_MS - 60_000;

/**
 * D-279 (#192): a thread page re-polls every two seconds while a bubble on it
 * is in flight. Once every tick has landed it re-polls only when the page
 * shows media links, at THREAD_MEDIA_LINK_INTERVAL_MS, because those links
 * expire (D-322 ruling 7, which amends D-279 ruling 2). A settled thread with
 * no links does not re-poll at all.
 *
 * THE FLAKE THIS CLOSES. The tick is a plain render of `message.status`
 * (inbox.tsx's `data-status`), and this query had no interval, no SSE and no
 * invalidation on send — so a bubble showed whatever status the page fetch
 * happened to catch, forever. The send sweep walks every active tenant per
 * pass (194 on staging, measured 2026-09-13), so a thread fetched a few
 * seconds before its tenant's turn painted `queued` and never repainted, while
 * the worker's own log said `claimed 1, sent 1`. M17-S02 and M17-S13 waited
 * 30 s for a `sent` that had already happened. One spec per run, whichever
 * fetched inside the window.
 *
 * WHY POLL AND NOT SSE, and why only while in flight. A manager watching a
 * thread expects the tick to flip without reloading — that is the promise
 * §4.1's tick ladder makes — but a settled thread has nothing to learn, and
 * the ≤10-manager shared inbox already re-polls its LIST at 60 s. Two seconds
 * while something is queued is invisible as load and closes the window;
 * `false` once everything has landed costs nothing. The one thing a settled
 * page still has to learn is a fresh media link, which it re-reads every
 * four minutes (D-322 ruling 7). SSE is module 04's floor
 * feed shape and is the right escalation if a thread ever needs sub-second
 * ticks, which nothing in spec 17 asks for.
 *
 * Exported as a pure function so the rule is unit-pinned on the page shape it
 * reads, not on a timer in a rendered screen.
 */
export function threadRefetchInterval(
  page:
    | {
        items: readonly {
          status: string;
          media?: readonly { download_url: string | null }[];
        }[];
      }
    | undefined,
): number | false {
  if (page === undefined) return false;
  if (page.items.some((m) => IN_FLIGHT.has(m.status))) return THREAD_LIVE_INTERVAL_MS;
  // A settled thread still holds links that expire (D-322 ruling 7): the
  // registry's download URL lives five minutes. A manager who reads a thread longer than
  // that and then opens a document would otherwise get the store's
  // AccessDenied page, so the page refetches before its links run out.
  const holdsLinks = page.items.some((m) =>
    (m.media ?? []).some((item) => item.download_url !== null),
  );
  return holdsLinks ? THREAD_MEDIA_LINK_INTERVAL_MS : false;
}

/**
 * Named, and typed by its own return, so that TanStack can pin the page type
 * BEFORE it sees the `refetchInterval` callback below. Handing v5 a function
 * for `refetchInterval` inside the same literal that also infers `TData` from
 * an inline `queryFn` collapses that inference to `unknown` — and every
 * `page.data.items` read in inbox.tsx failed to typecheck on the first cut of
 * D-279 for exactly that reason. A named function is the fix, not a cast.
 */
async function fetchThreadPage(
  conversationId: string,
  cursor: string | undefined,
  signal: AbortSignal,
  limit?: number,
) {
  const query = {
    ...(cursor !== undefined ? { cursor } : {}),
    ...(limit !== undefined ? { limit } : {}),
  };
  const res = await client.GET("/v1/messaging/conversations/{id}/messages", {
    signal,
    params: {
      path: { id: conversationId },
      ...(Object.keys(query).length > 0 ? { query } : {}),
    },
  });
  return unwrap(res, "Could not load the messages");
}
export type ThreadPage = Awaited<ReturnType<typeof fetchThreadPage>>;

/** The most rows one read may ask for: the route's own bound (§5, at most
 * 100). */
const THREAD_READ_MAX = 100;

/** How many reads one re-read of page one may take after its first, to reach
 * the message it last ended on. A burst larger than the bound re-reads page
 * one as far as the bound and the older pages chain on from there, so
 * nothing is lost that "Show earlier" cannot fetch again. */
const PAGE_ONE_ANCHOR_HOPS = 5;

/** What a re-read of page one needs from the page one it replaces: how many
 * rows it held, its oldest message by `created_at` then id (where the re-read
 * must end), and the ids it held (to count how many of them a read has
 * already reached). */
interface PageOneShape {
  readonly length: number;
  readonly anchor: string;
  readonly ids: ReadonlySet<string>;
}

/** The shape of the page one a re-read replaces, or undefined when there is
 * none to keep (a first read, or a thread that had no message). */
function pageOneShape(page: ThreadPage | undefined): PageOneShape | undefined {
  let oldest: ThreadMessage | undefined;
  for (const m of page?.items ?? []) {
    if (
      oldest === undefined ||
      m.created_at < oldest.created_at ||
      (m.created_at === oldest.created_at && m.id < oldest.id)
    ) {
      oldest = m;
    }
  }
  if (page === undefined || oldest === undefined) return undefined;
  return {
    length: page.items.length,
    anchor: oldest.id,
    ids: new Set(page.items.map((m) => m.id)),
  };
}

/**
 * Page one, read so that it ends EXACTLY on the message it ended on before.
 * TanStack re-reads an infinite query's pages in order, each next cursor
 * taken from the page just re-read, and keeps the page COUNT. So when new
 * messages have arrived, a plain re-read shifts every page older by that
 * many rows and the oldest loaded messages drop off the pane.
 *
 * The first read asks for as many rows as page one held. When nothing has
 * arrived it ends on the old oldest message and that is the whole cost: one
 * read. When `k` messages have arrived, the first `k` rows of that read are
 * new, so the read stops `k` rows short. The re-read then reads on from the
 * cursor the first read ended on and asks for exactly the rows page one
 * still lacks, so page one grows by the `k` new messages and no more. A
 * keyset cursor does not move when another message lands between the two
 * reads, which a second read from the top would. When a read overshoots the
 * old oldest message (a burst read at the route's bound, or a message gone
 * from the thread), that read is taken again with a limit that ends on it,
 * so the pages after page one chain on from the same place as before.
 */
async function fetchThreadPageOne(
  conversationId: string,
  shape: PageOneShape | undefined,
  signal: AbortSignal,
): Promise<ThreadPage> {
  if (shape === undefined) return fetchThreadPage(conversationId, undefined, signal);
  const items: ThreadMessage[] = [];
  let cursor: string | undefined;
  let limit = Math.min(Math.max(shape.length, 1), THREAD_READ_MAX);
  for (let hop = 0; ; hop += 1) {
    let page = await fetchThreadPage(conversationId, cursor, signal, limit);
    const at = page.items.findIndex((m) => m.id === shape.anchor);
    if (at >= 0 && at < page.items.length - 1) {
      page = await fetchThreadPage(conversationId, cursor, signal, at + 1);
    }
    items.push(...page.items);
    const next = page.next_cursor ?? undefined;
    if (at >= 0 || next === undefined || hop === PAGE_ONE_ANCHOR_HOPS) {
      return { ...page, items };
    }
    cursor = next;
    // The rows page one held that this re-read has not reached yet. Until
    // one of them shows, every row read so far is new and how many more are
    // is unknown, so the next read asks for the route's bound.
    const reached = items.filter((m) => shape.ids.has(m.id)).length;
    const lacking = shape.length - reached;
    limit = reached > 0 && lacking > 0 ? Math.min(lacking, THREAD_READ_MAX) : THREAD_READ_MAX;
  }
}

/**
 * Every loaded PAGE of a thread's messages, newest page first (§5), as ONE
 * infinite query. "Show earlier" is `fetchNextPage`. The poll re-reads every
 * loaded page, in order from page one, each next cursor taken from the page
 * just re-read, so an older page's media links are re-minted on the same
 * pace as page one's (D-322 ruling 7). One observer per page was the first
 * cut. Once "Show earlier" moved the pane's single observer to the older
 * page, page one's fresh links were never folded into the pane and the pages
 * between lost their observer, so their links expired on screen.
 *
 * The pace is judged on every loaded message at once, so a bubble in flight
 * on page one re-reads every loaded page every two seconds until it lands.
 * A queued send lands in seconds and a manager rarely holds more than a few
 * pages, so the cost stays small.
 *
 * A re-read keeps every message the pane had loaded. Page one is re-read
 * so that it ends on the message it ended on before (`fetchThreadPageOne`),
 * so a new message, the manager's own send or an inbound reply, grows page
 * one by exactly that one row and the oldest loaded message stays on the
 * pane. A re-read that finds no new message costs one read per loaded page,
 * and one that finds some costs one more read for page one.
 *
 * `enabled` exists for the inbox's render-once gate, which observes the same
 * query at SCREEN level (same key, one fetch, one cache entry) and must not
 * fire the read for a caller the permission mirror refuses (D-088: no request
 * built to 403).
 */
export function useThreadMessagePages(conversationId: string, enabled = true) {
  const qc = useQueryClient();
  const queryKey = messagingKeys.threadPages(conversationId);
  return useInfiniteQuery({
    queryKey,
    enabled: enabled && conversationId.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      pageParam === undefined
        ? fetchThreadPageOne(
            conversationId,
            // The cache still holds the previous pages while they are re-read.
            pageOneShape(qc.getQueryData<{ pages: ThreadPage[] }>(queryKey)?.pages[0]),
            signal,
          )
        : fetchThreadPage(conversationId, pageParam, signal),
    getNextPageParam: (lastPage: ThreadPage) => lastPage.next_cursor ?? undefined,
    // Live while a tick can still move (D-279), and slow while any loaded
    // page shows media links that expire (D-322). See threadRefetchInterval.
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      return pages === undefined
        ? false
        : threadRefetchInterval({ items: pages.flatMap((page) => page.items) });
    },
  });
}

/** The thread pane's bubbles: every loaded page folded by id, newest page
 * first, so an id that two pages both carry keeps one row. Pure, so the pane
 * derives its rows from the query and never holds a copy that a refetch can
 * leave behind. */
export function foldThreadPages(
  pages: readonly ThreadPage[] | undefined,
): readonly ThreadMessage[] {
  if (pages === undefined) return [];
  let rows: readonly ThreadMessage[] = [];
  for (const page of pages) rows = foldPagedRows(rows, page.items);
  return rows;
}

/** The §4.1 composer send — free-text, media, or template; the server runs
 * the §3.3 policy gates either way. 409 WA_WINDOW_CLOSED surfaces through
 * `RequestError.code` and the composer swaps to the template picker. */
export function useSendReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { conversationId: string; body: ThreadSendRequest }) => {
      const res = await client.POST("/v1/messaging/conversations/{id}/messages", {
        params: {
          path: { id: input.conversationId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: input.body,
      });
      return unwrap(res, "Could not send the message");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(input.conversationId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.conversation(input.conversationId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.conversationLists() });
    },
  });
}

/** §5 PATCH — assign / snooze / close / claim / set primary order. A claim
 * also re-reads the thread's messages: on an email thread it moves the
 * sender's held files into the thread's stored files (D-327 ruling 7), so the
 * bubbles that listed them as waiting must read them again. */
export function usePatchConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: ConversationPatchRequest }) => {
      const res = await client.PATCH("/v1/messaging/conversations/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not update the conversation");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: messagingKeys.conversation(input.id) });
      void qc.invalidateQueries({ queryKey: messagingKeys.conversationLists() });
      if (input.body.customer_contact_id !== undefined) {
        void qc.invalidateQueries({ queryKey: messagingKeys.thread(input.id) });
      }
    },
  });
}

/** Zero the unread counter — fired when a thread is OPENED (§5), so the list
 * badge and the head both refresh. */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/messaging/conversations/{id}/read", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not mark the conversation read");
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: messagingKeys.conversation(id) });
      void qc.invalidateQueries({ queryKey: messagingKeys.conversationLists() });
    },
  });
}

/** Manual retry of a failed message (§5; 409 MESSAGE_NOT_RETRYABLE). */
export function useRetryMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; conversationId: string }) => {
      const res = await client.POST("/v1/messaging/messages/{id}/retry", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not retry the message");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(input.conversationId) });
    },
  });
}

/** The §4.1 attach flow: mint the presigned PUT through the API, then PUT the
 * bytes straight at S3 (the one sanctioned non-client fetch — see header).
 * Resolves to the tenant-prefixed `s3_key` the reply send carries. */
export function useUploadAttachment() {
  return useMutation({
    mutationFn: async (
      file: File,
    ): Promise<{ s3Key: string; filename: string; byteSize: number }> => {
      const minted = await client.POST("/v1/messaging/media/upload-url", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { filename: file.name, mime_type: file.type, byte_size: file.size },
      });
      const grant = unwrap(minted, "Could not upload the attachment");
      const put = await fetch(grant.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!put.ok) {
        // S3 answers XML, not problem+json — the note renders the area's own
        // sentence and no fake server title.
        throw new RequestError(undefined, put.status, "Could not upload the attachment");
      }
      return { s3Key: grant.s3_key, filename: file.name, byteSize: file.size };
    },
  });
}

// ---------------------------------------------------------------------------
// accounts + health + webhook log — the §4.4 settings screen (part-1 routes
// plus this slice's webhook-events tail)
// ---------------------------------------------------------------------------

export function useChannelAccounts(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.accounts(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/accounts", { signal, params: {} });
      return unwrap(res, "Could not load the settings").items;
    },
  });
}

export function useMessagingHealth(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.health(),
    enabled,
    refetchInterval: 60_000,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/health", { signal, params: {} });
      return unwrap(res, "Could not load the delivery health").items;
    },
  });
}

/** §4.4's log tail (last ≤50). Payloads ride ONLY on error rows. */
export function useWebhookEvents(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.webhookLog(),
    enabled,
    refetchInterval: 60_000,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/webhook-events", {
        signal,
        params: { query: { limit: 50 } },
      });
      return unwrap(res, "Could not load the webhook log").items;
    },
  });
}

/** §3.1 connect — Telegram is the live path; `provider='whatsapp'` answers
 * 422 WA_PARKED while D-232 stands (the card states the parking instead). */
export function useConnectTelegram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (botToken: string) => {
      const res = await client.POST("/v1/messaging/accounts", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { provider: "telegram", telegram_bot_token: botToken },
      });
      return unwrap(res, "Could not connect the bot");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.accounts() });
      void qc.invalidateQueries({ queryKey: messagingKeys.health() });
    },
  });
}

export function useDisconnectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/messaging/accounts/{id}/disconnect", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not disconnect");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.accounts() });
      void qc.invalidateQueries({ queryKey: messagingKeys.health() });
    },
  });
}

/** §4.4 Reconnect — re-runs getWebhookInfo/setWebhook; the probe result
 * carries the provider's own last-error text VERBATIM as a typed field. On
 * the email account it checks the receiving rule set again (D-327 ruling 19)
 * and writes this tenant's health row, which the Email intake card's strip
 * reads, so the health snapshot is read again too. */
export function useProbeAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/messaging/accounts/{id}/probe", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not probe the connection");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.accounts() });
      void qc.invalidateQueries({ queryKey: messagingKeys.health() });
    },
  });
}

// ---------------------------------------------------------------------------
// email intake — the §4.4 Email intake card, the §4.2 Unverified mail list and
// the held-part download (P3-E2 PR E2-2e, D-327)
// ---------------------------------------------------------------------------

/** The tenant's intake: on or off, and its fixed address (§5). */
export function useEmailIntake(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.emailIntake(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/email-intake", { signal, params: {} });
      return unwrap(res, "Could not load the email intake");
    },
  });
}

/** Turn intake on or off (§5 enable/disable, D-327 ruling 5). 409
 * EMAIL_INTAKE_UNAVAILABLE reaches the card through `RequestError.code`. The
 * answer is the new intake state, written straight into the cache; the
 * account list and the health snapshot are read again, because both carry
 * the email account the switch moved. */
export function useSetEmailIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (on: boolean) => {
      const res = on
        ? await client.POST("/v1/messaging/email-intake/enable", {
            params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          })
        : await client.POST("/v1/messaging/email-intake/disable", {
            params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          });
      return unwrap(res, on ? "Could not turn email intake on" : "Could not turn email intake off");
    },
    onSuccess: (data) => {
      qc.setQueryData(messagingKeys.emailIntake(), data);
      void qc.invalidateQueries({ queryKey: messagingKeys.emailIntake() });
      void qc.invalidateQueries({ queryKey: messagingKeys.accounts() });
      void qc.invalidateQueries({ queryKey: messagingKeys.health() });
    },
  });
}

/** §4.2's Unverified mail list: the held mail, newest first, in the module's
 * paging (25 to a page). "Show more" is `fetchNextPage`. It re-reads on the
 * inbox list's minute pace, because another manager may release or discard a
 * row while this one is open. */
export function useEmailQuarantine(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: messagingKeys.emailQuarantine(),
    enabled,
    refetchInterval: 60_000,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/messaging/email-quarantine", {
          signal,
          params: { query: pageParam !== undefined ? { cursor: pageParam } : {} },
        }),
        "Could not load the unverified mail",
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

/** What a release or a discard leaves stale: the list itself, and, for a
 * release, every thread list, head and message page, because the mail now
 * sits on a thread (the sender's own, or a new triage thread). */
function invalidateAfterDecision(qc: ReturnType<typeof useQueryClient>, released: boolean): void {
  void qc.invalidateQueries({ queryKey: messagingKeys.emailQuarantine() });
  if (!released) return;
  void qc.invalidateQueries({ queryKey: messagingKeys.conversationLists() });
  void qc.invalidateQueries({ queryKey: messagingKeys.conversationHeads() });
  void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
}

/** Release a held mail (§5, D-327 ruling 18). The mailbox is named by its
 * index into the receipt's mailboxes, never by its value. A 409
 * EMAIL_RECEIPT_NOT_HELD means another person decided the mail first, so the
 * list is read again on any refusal as well as on success. */
export function useReleaseHeldMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; mailboxIndex: number | undefined }) => {
      const res = await client.POST("/v1/messaging/email-quarantine/{id}/release", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.mailboxIndex !== undefined ? { mailbox_index: input.mailboxIndex } : {},
      });
      return unwrap(res, "Could not release the mail");
    },
    onSuccess: () => {
      invalidateAfterDecision(qc, true);
    },
    onError: (error) => {
      if (error instanceof RequestError && error.code === "EMAIL_RECEIPT_NOT_HELD") {
        invalidateAfterDecision(qc, false);
      }
    },
  });
}

/** Discard a held mail (§5). Its text cannot come back. */
export function useDiscardHeldMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/messaging/email-quarantine/{id}/discard", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not discard the mail");
    },
    onSuccess: () => {
      invalidateAfterDecision(qc, false);
    },
    onError: (error) => {
      if (error instanceof RequestError && error.code === "EMAIL_RECEIPT_NOT_HELD") {
        invalidateAfterDecision(qc, false);
      }
    },
  });
}

/** Mint the 5-minute link for one held part (§5 held-part download), named
 * by the receipt that holds it and the part's index. A read, sent only when a
 * person presses Download, so a link is never minted for a file nobody
 * opens. */
export function useHeldPartLink() {
  return useMutation({
    mutationFn: async (input: { receiptId: string; index: number }) => {
      const res = await client.GET("/v1/messaging/email-receipts/{id}/parts/{index}/download", {
        params: { path: { id: input.receiptId, index: input.index } },
      });
      return unwrap(res, "Could not download the file");
    },
  });
}

function subscribeOnline(listener: () => void): () => void {
  return onlineManager.subscribe(listener);
}

function browserOnline(): boolean {
  return onlineManager.isOnline();
}

/** Is the browser online, as react-query sees it? The email surfaces disable
 * their writes while it is not (spec 17 §4.2 and §4.4: offline disables
 * Release, Discard and the intake switch), rather than letting a press wait
 * on a network that is gone. */
export function useMessagingOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, browserOnline, () => true);
}

// ---------------------------------------------------------------------------
// templates — the §4.3 catalog (part-1 routes)
// ---------------------------------------------------------------------------

/** Pages the whole-list read walks before it stops (100 rows each). A
 * tenant's templates are authored by people, so 5,000 is far past any real
 * catalog; the cap keeps a runaway cursor from spinning the tab. */
export const TEMPLATE_WALK_MAX_PAGES = 50;

/**
 * EVERY template matching `query`, walked page by page along `next_cursor`
 * (task #278). For a reader that filters client-side, such as the §4.1
 * composer's template picker, which must offer the tenant's whole active
 * set. Until #278 this read took the first 100 by code and stopped, so once
 * a tenant held 100 codes sorting ahead of `link_welcome` (every M17-S13 run
 * leaves a deactivated `e2e_m17_s13_…`), the picker offered nothing real.
 * `truncated` is true only when the walk hit TEMPLATE_WALK_MAX_PAGES.
 * System templates are ensured per-tenant on first read (§4.3 "empty state:
 * impossible", done as data), so a healthy read never answers empty.
 */
export function useMessagingTemplates(query: TemplatesQuery, enabled = true) {
  return useQuery({
    queryKey: messagingKeys.templates(query),
    enabled,
    queryFn: ({ signal }) =>
      walkTemplatePages(async (cursor) =>
        unwrap(
          await client.GET("/v1/messaging/templates", {
            signal,
            params: {
              query: { ...query, limit: 100, ...(cursor !== undefined ? { cursor } : {}) },
            },
          }),
          "Could not load the templates",
        ),
      ),
  });
}

/** Follow `next_cursor` from the first page to the last, at most
 * TEMPLATE_WALK_MAX_PAGES pages, and answer every row in server order. */
export async function walkTemplatePages(
  fetchPage: (cursor: string | undefined) => Promise<{
    items: MessageTemplate[];
    next_cursor: string | null;
  }>,
): Promise<{ items: MessageTemplate[]; truncated: boolean }> {
  const items: MessageTemplate[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < TEMPLATE_WALK_MAX_PAGES; page += 1) {
    const body = await fetchPage(cursor);
    items.push(...body.items);
    if (body.next_cursor === null) return { items, truncated: false };
    cursor = body.next_cursor;
  }
  return { items, truncated: true };
}

/**
 * The §4.3 catalog's read: cursor pages of 25, accumulated on demand (task
 * #278). The screen states how many rows it holds and offers "Show more"
 * while `next_cursor` says more remain, so every template can be reached and
 * what is on screen is always a count the reader was told. Deliberately not
 * auto-draining: that is `useMessagingTemplates`, for the picker.
 */
export function useMessagingTemplatePages(query: TemplatesQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...messagingKeys.templates(query), "pages"] as const,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/messaging/templates", {
          signal,
          params: {
            query: {
              ...query,
              limit: TEMPLATE_PAGE_SIZE,
              ...(pageParam !== undefined ? { cursor: pageParam } : {}),
            },
          },
        }),
        "Could not load the templates",
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

/** Rows per §4.3 catalog page: §5's default page size. */
export const TEMPLATE_PAGE_SIZE = 25;

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TemplateCreateRequest) => {
      const res = await client.POST("/v1/messaging/templates", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the template");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: messagingKeys.templateLists() }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: TemplateUpdateRequest }) => {
      const res = await client.PATCH("/v1/messaging/templates/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the template");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: messagingKeys.templateLists() }),
  });
}

/** Upsert one (provider, locale) variant body. Upserting a WA variant resets
 * its meta_status to `draft` (§5 — dormant Meta bookkeeping while D-232
 * stands). */
export function usePutVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      provider: "telegram" | "whatsapp";
      locale: Locale;
      body: VariantPutRequest;
    }) => {
      const res = await client.PUT("/v1/messaging/templates/{id}/variants/{provider}/{locale}", {
        params: {
          path: { id: input.templateId, provider: input.provider, locale: input.locale },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: input.body,
      });
      return unwrap(res, "Could not save the variant");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: messagingKeys.templateLists() }),
  });
}

// ---------------------------------------------------------------------------
// identities + link codes — the §4.5 widget (part-1 routes)
// ---------------------------------------------------------------------------

/** Identities for one owner (the §4.5 linked-status read the customer 360's
 * contact block embeds). Addresses arrive MASKED unless the caller holds
 * `messaging.address.read` — the server decides; the widget renders what it
 * is given. */
export function useIdentities(query: IdentitiesQuery, enabled = true) {
  return useQuery({
    queryKey: messagingKeys.identities(query),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/messaging/identities", { signal, params: { query } });
      return unwrap(res, "Could not load the messaging identities").items;
    },
  });
}

/** §3.2.1 issue — the plaintext code exists in the RESPONSE and nowhere else
 * (SHA-256 stored), which is why the modal warns before it closes. */
export function useIssueLinkCode() {
  return useMutation({
    mutationFn: async (input: {
      ownerKind: "customer_contact" | "user";
      ownerId: string;
      provider: "telegram" | "whatsapp";
    }) => {
      const res = await client.POST("/v1/messaging/identities/link-codes", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { owner_kind: input.ownerKind, owner_id: input.ownerId, provider: input.provider },
      });
      return unwrap(res, "Could not create the link code");
    },
  });
}

export function useRevokeIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const res = await client.POST("/v1/messaging/identities/{id}/revoke", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason: input.reason },
      });
      return unwrap(res, "Could not revoke the identity");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: messagingKeys.identityLists() }),
  });
}

// ---------------------------------------------------------------------------
// reference reads for the §4.1 context panel (module-09/16 routes already in
// the committed client)
// ---------------------------------------------------------------------------

/** Reference reads decorate, not gate: five minutes fresh, never retried
 * into an error card (the sales/production REF posture). */
const REF_OPTIONS = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: false } as const;

export type CustomerSummaryRow = S["Customer"];
export type Customer360 = S["Customer360Response"];

export const messagingRefKeys = {
  members: () => ["messaging", "ref", "members"] as const,
  customerSearch: (q: string) => ["messaging", "ref", "customers", q] as const,
  customerContacts: (id: string) => ["messaging", "ref", "customer", id] as const,
};

/**
 * User id → display name for the assign control and the assignee line — the
 * sales useOwnerIndex clone (an office-lib-style clone, not a cross-area hook
 * import). ONE list read, ONLY when the caller may make it: `GET /v1/members`
 * rides `identity.view_members`, which of the §4.1 audience only ga/fa hold
 * (`perms.isAdmin` is the mirror). Everyone else renders the short id in a
 * `<code>` chip — an identifier, never a broken name — and sales' assign
 * control is the `"me"` PATCH arm, which needs no directory and no self-id.
 */
export function useMemberIndex(enabled: boolean) {
  const query = useQuery({
    ...REF_OPTIONS,
    queryKey: messagingRefKeys.members(),
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  const byId = new Map<string, string>();
  for (const member of query.data?.items ?? []) byId.set(member.user_id, member.display_name);
  return { byId, members: query.data?.items ?? [], isPending: enabled && query.isPending };
}

/** §4.2's attach search, step 1: customers by name — module 09's own list
 * read, which the whole §4.1 working audience may make (it rides
 * `sales.read`). Fired only from the claim panel, only with a term. */
export function useCustomerSearch(q: string, enabled: boolean) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: messagingRefKeys.customerSearch(q),
    enabled: enabled && q.trim().length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/customers", {
        signal,
        params: { query: { q: q.trim(), limit: 10 } },
      });
      return unwrap(res, "Could not search the customers").items;
    },
  });
}

/** §4.2's attach search, step 2: the picked customer's CONTACTS — the §5
 * claim arm names a contact (`customer_contact_id`), and the 360 read is the
 * one route that carries them. */
export function useCustomerContacts(customerId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: messagingRefKeys.customerContacts(customerId),
    enabled: customerId.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/customers/{id}", {
        signal,
        params: { path: { id: customerId } },
      });
      return unwrap(res, "Could not load the customer");
    },
  });
}
